/**
 * Signing Guided Flow — DocuSign-style step-through field completion.
 *
 * Walks a signer through all their placed fields one at a time:
 * 1. Highlights the current field with a pulsing indicator
 * 2. Shows field type (sign, initial, date, text) and progress
 * 3. Auto-scrolls to the next field (across pages)
 * 4. Captures value per field type (signature/initials → SignatureCapture,
 *    date → auto-fill, text → inline input)
 * 5. Calls completeCeremonyField mutation per field
 * 6. Shows completion when all fields are done
 *
 * FERP Constraints:
 * - Mantine v8.x ONLY
 * - Backend IDs only (field_index from ceremony)
 * - Immutable audit trail per field completion
 * - useAuth() for signer identity
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Stack,
  Group,
  Button,
  Text,
  Paper,
  Progress,
  Badge,
  Alert,
  TextInput,
  ThemeIcon,
  Transition,
} from '@mantine/core';
import {
  IconSignature,
  IconLetterCase,
  IconCalendar,
  IconTextSize,
  IconArrowRight,
  IconArrowLeft,
  IconCheck,
  IconCircleCheck,
} from '@tabler/icons-react';
import { SignatureCapture } from './SignatureCapture';

// =============================================================================
// TYPES
// =============================================================================

export interface GuidedField {
  /** Index in the ceremony's fields array (used for completeCeremonyField mutation). */
  fieldIndex: number;
  /** Which participant this field belongs to. */
  participantIndex: number;
  /** Field type. */
  fieldType: 'SIGNATURE' | 'INITIALS' | 'DATE_SIGNED' | 'TEXT_FIELD';
  /** Page number (1-indexed). */
  page: number;
  /** Left edge (relative 0–1). */
  x: number;
  /** Top edge (relative 0–1). */
  y: number;
  /** Width (relative 0–1). */
  width: number;
  /** Height (relative 0–1). */
  height: number;
  /** Whether this field has been completed. */
  completed: boolean;
}

export interface SigningGuidedFlowProps {
  /** Fields for the current signer to complete (filtered to their participant index). */
  fields: GuidedField[];
  /** Callback when a field is completed. Returns the updated ceremony. */
  onCompleteField: (fieldIndex: number, value: string | null) => Promise<void>;
  /** Callback when ALL fields are completed. */
  onAllCompleted: () => void;
  /** Document name for display. */
  documentName?: string;
  /** Whether a field completion is in progress. */
  submitting?: boolean;
  /** Signer's display name (for signature capture modal). */
  signerName?: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const FIELD_TYPE_CONFIG: Record<string, {
  label: string;
  action: string;
  icon: typeof IconSignature;
  color: string;
}> = {
  SIGNATURE: {
    label: 'Signature',
    action: 'Sign here',
    icon: IconSignature,
    color: 'blue',
  },
  INITIALS: {
    label: 'Initials',
    action: 'Initial here',
    icon: IconLetterCase,
    color: 'grape',
  },
  DATE_SIGNED: {
    label: 'Date',
    action: 'Add date',
    icon: IconCalendar,
    color: 'teal',
  },
  TEXT_FIELD: {
    label: 'Text',
    action: 'Enter text',
    icon: IconTextSize,
    color: 'orange',
  },
};

// =============================================================================
// COMPONENT
// =============================================================================

export function SigningGuidedFlow({
  fields,
  onCompleteField,
  onAllCompleted,
  documentName,
  submitting = false,
  signerName,
}: SigningGuidedFlowProps) {
  // Current field index within the signer's field list
  const [currentIdx, setCurrentIdx] = useState(() => {
    // Start at the first incomplete field
    const firstIncomplete = fields.findIndex((f) => !f.completed);
    return firstIncomplete >= 0 ? firstIncomplete : 0;
  });
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [textValue, setTextValue] = useState('');
  const [completedCount, setCompletedCount] = useState(
    () => fields.filter((f) => f.completed).length,
  );
  const [allDone, setAllDone] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);

  const totalFields = fields.length;
  const currentField = fields[currentIdx];
  const config = currentField ? FIELD_TYPE_CONFIG[currentField.fieldType] : null;
  const progressPercent = totalFields > 0 ? (completedCount / totalFields) * 100 : 0;

  // Sort fields by page, then y position for natural reading order
  const sortedFields = useMemo(() => {
    return [...fields].sort((a, b) => {
      if (a.page !== b.page) return a.page - b.page;
      return a.y - b.y;
    });
  }, [fields]);

  // Auto-scroll to current field indicator
  useEffect(() => {
    if (fieldRef.current) {
      fieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentIdx]);

  // Navigate to next incomplete field
  const goToNext = useCallback(() => {
    const nextIncomplete = fields.findIndex(
      (f, i) => i > currentIdx && !f.completed,
    );
    if (nextIncomplete >= 0) {
      setCurrentIdx(nextIncomplete);
    } else {
      // Wrap around to find any remaining incomplete
      const wrapIncomplete = fields.findIndex((f) => !f.completed);
      if (wrapIncomplete >= 0) {
        setCurrentIdx(wrapIncomplete);
      } else {
        setAllDone(true);
        onAllCompleted();
      }
    }
  }, [fields, currentIdx, onAllCompleted]);

  const goToPrev = useCallback(() => {
    for (let i = currentIdx - 1; i >= 0; i--) {
      if (!fields[i]?.completed) {
        setCurrentIdx(i);
        return;
      }
    }
  }, [fields, currentIdx]);

  // Handle field completion based on type
  const handleComplete = useCallback(
    async (value: string | null) => {
      if (!currentField) return;
      await onCompleteField(currentField.fieldIndex, value);
      setCompletedCount((c) => c + 1);
      goToNext();
    },
    [currentField, onCompleteField, goToNext],
  );

  // Handle signature/initials capture
  const handleSignature = useCallback(
    async (dataUrl: string, _signerId: string) => {
      setSignatureModalOpen(false);
      await handleComplete(dataUrl);
    },
    [handleComplete],
  );

  // Handle date field (auto-fill with today)
  const handleDateComplete = useCallback(async () => {
    const today = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    await handleComplete(today);
  }, [handleComplete]);

  // Handle text field
  const handleTextComplete = useCallback(async () => {
    if (!textValue.trim()) return;
    await handleComplete(textValue);
    setTextValue('');
  }, [textValue, handleComplete]);

  // All done state
  if (allDone || completedCount >= totalFields) {
    return (
      <Paper p="xl" withBorder radius="md" shadow="sm">
        <Stack align="center" gap="lg">
          <ThemeIcon size={80} color="green" radius="50%">
            <IconCircleCheck size={48} />
          </ThemeIcon>
          <Text size="xl" fw={700}>All Fields Completed!</Text>
          <Text c="dimmed" ta="center">
            You have completed all {totalFields} field{totalFields !== 1 ? 's' : ''}.
            {documentName && <> Your signature on <strong>{documentName}</strong> is being processed.</>}
          </Text>
          <Progress value={100} color="green" size="lg" w="100%" animated />
        </Stack>
      </Paper>
    );
  }

  if (!currentField || !config) {
    return (
      <Alert color="yellow">No fields to complete.</Alert>
    );
  }

  const FieldIcon = config.icon;
  const incompleteBeforeCurrent = fields
    .slice(0, currentIdx)
    .filter((f) => !f.completed).length;

  return (
    <Stack gap="md">
      {/* Progress bar */}
      <Paper p="sm" withBorder>
        <Group justify="space-between" mb={4}>
          <Text size="sm" fw={600}>
            Field {completedCount + 1} of {totalFields}
          </Text>
          <Badge
            color={config.color}
            leftSection={<FieldIcon size={12} />}
            size="lg"
          >
            {config.label}
          </Badge>
        </Group>
        <Progress
          value={progressPercent}
          color="blue"
          size="md"
          animated={submitting}
        />
      </Paper>

      {/* Current field indicator — this is the pulsing "sign here" target */}
      <Box ref={fieldRef}>
        <Transition mounted transition="slide-up" duration={300}>
          {(styles) => (
            <Paper
              p="lg"
              withBorder
              shadow="md"
              style={{
                ...styles,
                borderColor: `var(--mantine-color-${config.color}-5)`,
                borderWidth: 2,
              }}
            >
              <Stack gap="md">
                <Group>
                  <ThemeIcon
                    size={48}
                    color={config.color}
                    variant="light"
                    radius="md"
                  >
                    <FieldIcon size={28} />
                  </ThemeIcon>
                  <Stack gap={2}>
                    <Text size="lg" fw={700}>{config.action}</Text>
                    <Text size="sm" c="dimmed">
                      Page {currentField.page}
                      {documentName && ` — ${documentName}`}
                    </Text>
                  </Stack>
                </Group>

                {/* Field-type-specific action area */}
                {(currentField.fieldType === 'SIGNATURE' ||
                  currentField.fieldType === 'INITIALS') && (
                  <Button
                    size="xl"
                    color={config.color}
                    leftSection={<FieldIcon size={24} />}
                    onClick={() => setSignatureModalOpen(true)}
                    loading={submitting}
                    fullWidth
                    style={{
                      animation: 'ferp-pulse-btn 2s ease-in-out infinite',
                    }}
                  >
                    {currentField.fieldType === 'SIGNATURE'
                      ? 'Tap to Sign'
                      : 'Tap to Initial'}
                  </Button>
                )}

                {currentField.fieldType === 'DATE_SIGNED' && (
                  <Button
                    size="xl"
                    color={config.color}
                    leftSection={<IconCalendar size={24} />}
                    onClick={handleDateComplete}
                    loading={submitting}
                    fullWidth
                  >
                    Apply Today's Date
                  </Button>
                )}

                {currentField.fieldType === 'TEXT_FIELD' && (
                  <Group>
                    <TextInput
                      placeholder="Enter text..."
                      value={textValue}
                      onChange={(e) => setTextValue(e.currentTarget.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleTextComplete();
                      }}
                      style={{ flex: 1 }}
                      size="lg"
                    />
                    <Button
                      size="lg"
                      color={config.color}
                      onClick={handleTextComplete}
                      loading={submitting}
                      disabled={!textValue.trim()}
                    >
                      Apply
                    </Button>
                  </Group>
                )}
              </Stack>
            </Paper>
          )}
        </Transition>
      </Box>

      {/* Navigation */}
      <Group justify="space-between">
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={goToPrev}
          disabled={incompleteBeforeCurrent === 0}
        >
          Previous
        </Button>
        <Button
          variant="subtle"
          rightSection={<IconArrowRight size={16} />}
          onClick={goToNext}
          disabled={completedCount >= totalFields - 1}
        >
          Skip
        </Button>
      </Group>

      {/* Field overview — shows all fields with completion status */}
      <Paper p="sm" withBorder>
        <Text size="xs" fw={600} mb="xs" c="dimmed">
          All Fields
        </Text>
        <Group gap={6}>
          {sortedFields.map((field, i) => {
            const fc = FIELD_TYPE_CONFIG[field.fieldType];
            const isCurrent = field.fieldIndex === currentField.fieldIndex;
            return (
              <Badge
                key={field.fieldIndex}
                size="sm"
                color={field.completed ? 'green' : isCurrent ? fc?.color : 'gray'}
                variant={isCurrent ? 'filled' : field.completed ? 'light' : 'outline'}
                leftSection={
                  field.completed ? (
                    <IconCheck size={10} />
                  ) : fc ? (
                    <fc.icon size={10} />
                  ) : null
                }
                style={
                  isCurrent
                    ? { boxShadow: '0 0 0 2px var(--mantine-color-blue-3)' }
                    : undefined
                }
              >
                P{field.page}
              </Badge>
            );
          })}
        </Group>
      </Paper>

      {/* Signature capture modal (shared for Signature + Initials) */}
      <SignatureCapture
        opened={signatureModalOpen}
        onClose={() => setSignatureModalOpen(false)}
        onSign={handleSignature}
        documentName={
          currentField.fieldType === 'INITIALS'
            ? `Initials — Page ${currentField.page}`
            : documentName
        }
        signing={submitting}
      />

      {/* Pulse animation for the sign button */}
      <style>{`
        @keyframes ferp-pulse-btn {
          0%, 100% { box-shadow: 0 0 0 0 rgba(51, 154, 240, 0.4); }
          50% { box-shadow: 0 0 0 12px rgba(51, 154, 240, 0); }
        }
      `}</style>
    </Stack>
  );
}

// =============================================================================
// FIELD OVERLAY — renders on top of PDF to show field positions
// =============================================================================

export interface SigningFieldOverlayProps {
  /** All fields for the current signer. */
  fields: GuidedField[];
  /** Index of the current active field. */
  activeFieldIndex: number;
  /** Current page being displayed (1-indexed). */
  page: number;
  /** Click handler for a field (navigate to it). */
  onFieldClick?: (fieldIndex: number) => void;
}

/**
 * Renders field position indicators on top of the PDF.
 * Shows completed fields with a checkmark, current field with a pulse,
 * and upcoming fields as outlined placeholders.
 */
export function SigningFieldOverlay({
  fields,
  activeFieldIndex,
  page,
  onFieldClick,
}: SigningFieldOverlayProps) {
  const pageFields = fields.filter((f) => f.page === page);

  return (
    <Box style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {pageFields.map((field) => {
        const isCurrent = field.fieldIndex === activeFieldIndex;
        const fc = FIELD_TYPE_CONFIG[field.fieldType];

        return (
          <Box
            key={field.fieldIndex}
            role="button"
            tabIndex={0}
            aria-label={
              field.completed
                ? `${fc?.label} — completed`
                : isCurrent
                  ? `${fc?.action} — tap to complete`
                  : `${fc?.label} — upcoming`
            }
            style={{
              position: 'absolute',
              left: `${field.x * 100}%`,
              top: `${field.y * 100}%`,
              width: `${field.width * 100}%`,
              height: `${field.height * 100}%`,
              minHeight: 40,
              border: field.completed
                ? '2px solid var(--mantine-color-green-5)'
                : isCurrent
                  ? `2px solid var(--mantine-color-${fc?.color ?? 'blue'}-5)`
                  : '2px dashed var(--mantine-color-gray-4)',
              background: field.completed
                ? 'rgba(64, 192, 87, 0.12)'
                : isCurrent
                  ? 'rgba(51, 154, 240, 0.15)'
                  : 'rgba(0, 0, 0, 0.03)',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: onFieldClick && !field.completed ? 'pointer' : 'default',
              pointerEvents: 'auto',
              touchAction: 'manipulation',
              animation: isCurrent && !field.completed
                ? 'ferp-pulse 1.5s ease-in-out infinite'
                : undefined,
            }}
            onClick={() => {
              if (onFieldClick && !field.completed) {
                onFieldClick(field.fieldIndex);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && onFieldClick && !field.completed) {
                onFieldClick(field.fieldIndex);
              }
            }}
          >
            {field.completed ? (
              <IconCheck
                size={16}
                style={{ color: 'var(--mantine-color-green-6)' }}
              />
            ) : (
              <Text
                style={{
                  color: isCurrent
                    ? `var(--mantine-color-${fc?.color ?? 'blue'}-7)`
                    : 'var(--mantine-color-gray-5)',
                  fontWeight: 700,
                  fontSize: 11,
                  userSelect: 'none',
                }}
              >
                {isCurrent ? `✍ ${fc?.action}` : fc?.label}
              </Text>
            )}
          </Box>
        );
      })}

      <style>{`
        @keyframes ferp-pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(51, 154, 240, 0.4); }
          50% { opacity: 0.85; box-shadow: 0 0 0 8px rgba(51, 154, 240, 0); }
        }
      `}</style>
    </Box>
  );
}
